import { useContext, useState, useEffect } from 'react';
import { Box, Icon, IconButton, Stack, Typography } from '@ace-clublabs/shared-component-library';
import { MembershipAppStateContext } from '../../components/MembershipAppState/MembershipAppState';
import { ProductType } from '../../application/models/NewJoinSessionData';
import { getProductPrice, eligibleForRV, eligibleForRVUpgrade } from '../../utils/helpers';
import { AddOn } from '../CustomizeMembershipComponents/AddOn';
import { ClubContentName, getClubContent } from '../../utils/getClubContent';
import { CurrentPayPlan } from '@ace-clublabs/common-membership-api-types';
import { AddedInfoCard } from './AddedInfoCard';

export interface RVAddOnPros {
  setTooltipInfo: Function;
  setTooltipContent: Function;
  setDeleteInfo: Function;
  selectedRV: boolean;
  onUpgradeClick: () => void;
}

export const RVAddOn: (props: RVAddOnPros) => JSX.Element = ({
  setTooltipInfo,
  setTooltipContent,
  setDeleteInfo,
  selectedRV,
  onUpgradeClick,
}) => {
  const { membershipInfo, updateProducts, sessionData, isPriceCartLoading } = useContext(MembershipAppStateContext);
  const [isUpdating, setIsUpdating] = useState(false);
  const [displaySelected, setDisplaySelected] = useState(selectedRV);

  useEffect(() => {
    if (!isPriceCartLoading && isUpdating) {
      setDisplaySelected(selectedRV);
      setIsUpdating(false);
    }
  }, [isPriceCartLoading]);

  if (!eligibleForRV(membershipInfo)) {
    return <></>;
  }

  const hasToUpgrade = eligibleForRVUpgrade(membershipInfo, sessionData);
  const { billPlan } = sessionData.formData;
  const priceSuffix = billPlan === CurrentPayPlan.RECURRINGMONTHLY ? 'month' : 'year';

  return (
    <>
      <Box className="benefit-add-ons">
        <Stack direction="row" alignItems="center" gap="0.5rem">
          <Icon iconName="van-shuttle-duotone" sx={{ color: '#0C7A93', fontSize: '1.5rem' }} />
          <Typography className="benefit-add-ons-title add-rv-title" variant="b1Medium">
            Roadside for RV/motorcycle
          </Typography>
        </Stack>
        <IconButton
          aria-label="RVs Trailers & Motorcycle info"
          className="add-RV-info-icon"
          onClick={() => {
            setTooltipInfo({
              tooltipTitle: 'Roadside for RV/motorcycle',
            });
            setTooltipContent(
              hasToUpgrade
                ? 'Optional roadside for RV/motorcycle is not available at the Classic benefit level.'
                : getClubContent(ClubContentName.RVToolTip, sessionData.clubCode!)
            );
          }}
        >
          <Icon iconName="circle-info-regular" sx={{ color: '#707070' }} />
        </IconButton>
      </Box>
      {hasToUpgrade ? (
        <Box className="rv-upgrade">
          <AddOn
            onAdd={onUpgradeClick}
            text="Upgrade to Plus or Premier"
            price={getProductPrice(membershipInfo, ProductType.RV, sessionData)}
            icon="membership-card-solid"
            type={ProductType.RV}
            showPrice={false}
            isPriceCartLoading={isPriceCartLoading}
          />
        </Box>
      ) : displaySelected ? (
        <AddedInfoCard
          className="rv-added-card"
          isUpdating={isUpdating}
          deleteButtonOnClick={() => {
            setDeleteInfo({
              title: 'Are you sure you want to remove roadside for RV/motorcycle?',
              confirmRemove: () => {
                setIsUpdating(true);
                updateProducts('remove', ProductType.RV);
              },
            });
          }}
          title="Added"
          originalPrice={getProductPrice(membershipInfo, ProductType.RV, sessionData)}
          priceSuffix={priceSuffix}
          content={
            <span>
              Benefits available 7 days after you join.
            </span>
          }
        />
      ) : (
        <AddOn
          onAdd={() => {
            setIsUpdating(true);
            updateProducts('add', ProductType.RV);
          }}
          text="Add RV/motorcycle"
          price={getProductPrice(membershipInfo, ProductType.RV, sessionData)}
          icon="van-shuttle-regular"
          type={ProductType.RV}
          priceSuffix={priceSuffix}
          isPriceCartLoading={isPriceCartLoading}
          isUpdating={isUpdating}
        />
      )}
    </>
  );
};
